// Data access for the events module panels. Pages stay thin; every
// /tenant/events* call lives here. Shapes mirror apps/api/src/events-model.ts
// (docs/events-module.md is the contract).
import type { ApiClient } from "./api";

export type EventWeekday = "MO" | "TU" | "WE" | "TH" | "FR" | "SA" | "SU";

export const EVENT_WEEKDAYS: { code: EventWeekday; label: string }[] = [
  { code: "MO", label: "Mon" },
  { code: "TU", label: "Tue" },
  { code: "WE", label: "Wed" },
  { code: "TH", label: "Thu" },
  { code: "FR", label: "Fri" },
  { code: "SA", label: "Sat" },
  { code: "SU", label: "Sun" },
];

export interface EventRecurrenceEnds {
  type: "never" | "on" | "count";
  date?: string;
  n?: number;
}

export interface EventRecurrenceException {
  from: string;
  to: string;
}

export interface EventRecurrence {
  freq: "day" | "week" | "month" | "year";
  interval: number;
  by_weekday?: EventWeekday[];
  monthly_mode?: "day_of_month" | "nth_weekday";
  nth?: number;
  weekday?: EventWeekday;
  ends: EventRecurrenceEnds;
  exceptions: EventRecurrenceException[];
}

export interface EventItem {
  id: string;
  title: string;
  featured: number;
  image_key: string | null;
  image_alt: string | null;
  date: string;
  end_date: string | null;
  all_day: number;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  /** Editor.js {"blocks":[...]} document, or "" — portal v1 edits paragraphs. */
  description: string;
  link_url: string | null;
  link_label: string | null;
  /** Stored recurrence JSON, or null for one-offs. */
  recurrence: string | null;
  published: number;
  created_at: string;
  updated_at: string;
}

export interface EventsFileSync {
  updated: boolean;
  reason: string | null;
}

export interface EventSavePayload {
  title?: string;
  featured?: 0 | 1;
  image_key?: string | null;
  image_alt?: string | null;
  date?: string;
  end_date?: string | null;
  all_day?: 0 | 1;
  start_time?: string | null;
  end_time?: string | null;
  location?: string | null;
  description?: string;
  link_url?: string | null;
  link_label?: string | null;
  recurrence?: EventRecurrence | null;
  published?: 0 | 1;
}

export async function fetchEvents(api: ApiClient): Promise<EventItem[]> {
  const data = await api.request<{ events: EventItem[] }>("/tenant/events");
  return data.events ?? [];
}

export interface EventsPageResult {
  events: EventItem[];
  total: number;
}

/** Paged list for the Events table; `query` is a list-controls query string. */
export async function fetchEventsPage(api: ApiClient, query: string): Promise<EventsPageResult> {
  const data = await api.request<{ events?: EventItem[]; total?: number }>(`/tenant/events?${query}`);
  return { events: data.events ?? [], total: data.total ?? 0 };
}

export async function fetchEvent(api: ApiClient, id: string): Promise<EventItem> {
  const data = await api.request<{ event: EventItem }>(`/tenant/events/${id}`);
  return data.event;
}

export async function createEvent(
  api: ApiClient,
  input: { title: string; date: string }
): Promise<{ id: string }> {
  return await api.request("/tenant/events", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function saveEvent(
  api: ApiClient,
  id: string,
  payload: EventSavePayload
): Promise<{ id: string; events_file: EventsFileSync }> {
  return await api.request(`/tenant/events/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteEvent(
  api: ApiClient,
  id: string
): Promise<{ events_file: EventsFileSync }> {
  return await api.request(`/tenant/events/${id}`, { method: "DELETE" });
}

export async function regenerateEventsFile(
  api: ApiClient
): Promise<{ events_file: EventsFileSync }> {
  return await api.request("/tenant/events/regenerate", { method: "POST" });
}

/** Parse a stored recurrence JSON string; null when absent or unparseable. */
export function parseEventRecurrence(stored: string | null): EventRecurrence | null {
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === "object" ? (parsed as EventRecurrence) : null;
  } catch {
    return null;
  }
}

/** Plain text -> the Editor.js paragraph document the API stores (v1 description editing). */
export function descriptionToDoc(text: string): string {
  const paragraphs = text
    .split(/\n{2,}|\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (paragraphs.length === 0) return "";
  return JSON.stringify({
    blocks: paragraphs.map((line) => ({ type: "paragraph", data: { text: line } })),
  });
}

/** Editor.js document -> plain text for the textarea (inverse of descriptionToDoc). */
export function docToDescription(doc: string): string {
  if (!doc) return "";
  try {
    const parsed = JSON.parse(doc);
    if (!parsed || !Array.isArray(parsed.blocks)) return "";
    return parsed.blocks
      .map((block: { type?: string; data?: { text?: string } }) =>
        block?.type === "paragraph" && typeof block.data?.text === "string"
          ? block.data.text.replace(/<[^>]*>/g, "")
          : ""
      )
      .filter(Boolean)
      .join("\n\n");
  } catch {
    return "";
  }
}

/** Human summary of a recurrence for the events list, e.g. "Every week on Mon, Wed". */
export function describeRecurrence(recurrence: EventRecurrence | null): string {
  if (!recurrence) return "";
  const unit = recurrence.interval === 1 ? recurrence.freq : `${recurrence.interval} ${recurrence.freq}s`;
  let text = `Every ${unit}`;
  if (recurrence.freq === "week" && recurrence.by_weekday?.length) {
    const labels = EVENT_WEEKDAYS.filter((d) => recurrence.by_weekday!.includes(d.code)).map((d) => d.label);
    text += ` on ${labels.join(", ")}`;
  }
  if (recurrence.freq === "month") {
    text += recurrence.monthly_mode === "nth_weekday" ? " (relative day)" : " (same day)";
  }
  if (recurrence.ends.type === "on") text += ` until ${recurrence.ends.date}`;
  if (recurrence.ends.type === "count") text += `, ${recurrence.ends.n}×`;
  return text;
}
